/**
 * Master design ingestion utilities for converting Canva Master designs to template descriptors
 */

import { parseAlias, surfaceOf, isValidAlias } from './aliasParser';
import { pxToMmRounded, bboxPxToMm } from './units';

export interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TextElementProps {
  text: string;
  fontSize: number;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  color?: string;
  textAlign?: string;
  textDecoration?: string;
  lineHeight?: number;
  letterSpacing?: number;
  textTransform?: string;
  regions?: any[]; // Full text regions with formatting
}

export interface ImageElementProps {
  imageRef?: string;
  imageUrl?: string;
  maskFlag?: boolean;
  cropData?: any;
  filters?: any;
  opacity?: number;
  blendMode?: string;
}

export interface ShapeElementProps {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDashArray?: number[];
  strokeLineCap?: string;
  strokeLineJoin?: string;
  paths?: any[]; // SVG path data
  viewBox?: any;
  gradientFill?: any;
  patternFill?: any;
}

export interface TemplateLayer {
  id: string;
  surface: 'front' | 'spine' | 'back';
  kind: 'text' | 'image' | 'shape';
  lock: boolean;
  metadata: {
    userEditable: boolean;
    alias: string;
  };
  props: {
    absXmm: number;
    absYmm: number;
    wMm: number;
    hMm: number;
    rotationDeg: number;
    opacity: number;
    // Text properties
    text?: string;
    fontFamily?: string;
    fontSizePx?: number;
    fontWeight?: string;
    fontStyle?: string;
    color?: string;
    textAlign?: string;
    textDecoration?: string;
    lineHeight?: number;
    letterSpacing?: number;
    textTransform?: string;
    regions?: any[]; // Complete text formatting data
    // Image properties
    imageRef?: string;
    imageUrl?: string;
    mask?: boolean;
    cropData?: any;
    filters?: any;
    blendMode?: string;
    // Shape properties
    fill?: string;
    stroke?: string;
    strokeWidthPx?: number;
    strokeDashArray?: number[];
    strokeLineCap?: string;
    strokeLineJoin?: string;
    paths?: any[]; // Complete SVG path data
    viewBox?: any;
    gradientFill?: any;
    patternFill?: any;
    // General visual properties
    shadow?: any;
    border?: any;
    borderRadius?: number;
  };
}

export interface CoverDescriptor {
  slug: string;
  dpi: number;
  coverSize: {
    widthMm: number;
    heightMm: number;
  };
  meta: {
    sourcePageSizePx: {
      widthPx: number;
      heightPx: number;
    };
    sourceScalePxPerMm: number;
  };
  layers: TemplateLayer[];
  createdAt: string;
  masterDesignId: string;
}

export interface IngestionResult {
  layers: TemplateLayer[];
  log: string[];
  aliasCount: number;
  elementCount: number;
}

/**
 * Determine element kind from Canva element type
 */
export function determineElementKind(elementType: string): 'text' | 'image' | 'shape' | null {
  switch (elementType.toLowerCase()) {
    case 'text':
    case 'richtext':
      return 'text';
    case 'image':
    case 'rect': // Rects with image fills are treated as images
      return 'image';
    case 'shape':
    case 'path':
    case 'circle':
    case 'rectangle':
      return 'shape';
    default:
      return null;
  }
}

/**
 * Extract text properties from Canva text element
 */
export function extractTextProps(element: any): TextElementProps {
  const props: TextElementProps = {
    text: element.text?.plaintext || element.text || 'Sample Text',
    fontSize: element.fontSize || 16
  };

  // Basic text properties
  if (element.fontFamily) props.fontFamily = element.fontFamily;
  if (element.fontWeight) props.fontWeight = element.fontWeight;
  if (element.fontStyle) props.fontStyle = element.fontStyle;
  if (element.color) props.color = element.color;
  if (element.textAlign) props.textAlign = element.textAlign;
  if (element.textDecoration) props.textDecoration = element.textDecoration;
  if (element.lineHeight) props.lineHeight = element.lineHeight;
  if (element.letterSpacing) props.letterSpacing = element.letterSpacing;
  if (element.textTransform) props.textTransform = element.textTransform;

  // Capture complete text regions with formatting
  if (element.text?.regions) {
    props.regions = element.text.regions;
  } else if (element.regions) {
    props.regions = element.regions;
  }

  console.log(`[ingest] Text element properties:`, {
    text: props.text,
    fontFamily: props.fontFamily,
    fontSize: props.fontSize,
    color: props.color,
    hasRegions: !!props.regions,
    regionsCount: props.regions?.length || 0
  });

  return props;
}

/**
 * Extract image properties from Canva image/rect element
 */
export function extractImageProps(element: any): ImageElementProps {
  const props: ImageElementProps = {};

  // Image reference and URL
  if (element.imageRef) props.imageRef = element.imageRef;
  if (element.fill?.mediaContainer?.imageRef) props.imageRef = element.fill.mediaContainer.imageRef;
  if (element.imageUrl) props.imageUrl = element.imageUrl;
  if (element.src) props.imageUrl = element.src;

  // Masking and cropping
  if (element.maskFlag !== undefined) props.maskFlag = element.maskFlag;
  if (element.cropData) props.cropData = element.cropData;
  if (element.crop) props.cropData = element.crop;

  // Visual effects
  if (element.filters) props.filters = element.filters;
  if (element.opacity !== undefined) props.opacity = element.opacity;
  if (element.blendMode) props.blendMode = element.blendMode;

  console.log(`[ingest] Image element properties:`, {
    hasImageRef: !!props.imageRef,
    hasImageUrl: !!props.imageUrl,
    hasCropData: !!props.cropData,
    hasFilters: !!props.filters,
    opacity: props.opacity,
    blendMode: props.blendMode
  });

  return props;
}

/**
 * Extract shape properties from Canva shape element
 */
export function extractShapeProps(element: any): ShapeElementProps {
  const props: ShapeElementProps = {};

  // Fill properties
  if (element.fill?.colorContainer?.color) props.fill = element.fill.colorContainer.color;
  if (element.fill?.color) props.fill = element.fill.color;
  if (element.fill?.gradient) props.gradientFill = element.fill.gradient;
  if (element.fill?.pattern) props.patternFill = element.fill.pattern;

  // Stroke properties
  if (element.stroke?.color) props.stroke = element.stroke.color;
  if (element.stroke?.width) props.strokeWidth = element.stroke.width;
  if (element.stroke?.dashArray) props.strokeDashArray = element.stroke.dashArray;
  if (element.stroke?.lineCap) props.strokeLineCap = element.stroke.lineCap;
  if (element.stroke?.lineJoin) props.strokeLineJoin = element.stroke.lineJoin;

  // Shape geometry
  if (element.paths) props.paths = element.paths;
  if (element.viewBox) props.viewBox = element.viewBox;

  console.log(`[ingest] Shape element properties:`, {
    fill: props.fill,
    stroke: props.stroke,
    strokeWidth: props.strokeWidth,
    hasGradient: !!props.gradientFill,
    hasPattern: !!props.patternFill,
    hasPaths: !!props.paths,
    pathsCount: props.paths?.length || 0
  });

  return props;
}

/**
 * Convert Canva element bounds to absolute millimeters
 */
export function convertElementBoundsAbsolute(element: any, dpi = 300): {
  absXmm: number;
  absYmm: number;
  wMm: number;
  hMm: number;
  rotationDeg: number;
  opacity: number;
} {
  const bounds = {
    left: element.left || element.x || 0,
    top: element.top || element.y || 0,
    width: element.width || 100,
    height: element.height || 100,
    rotation: element.rotation || 0,
    opacity: element.opacity !== undefined ? element.opacity : 1
  };

  return {
    absXmm: pxToMmRounded(bounds.left, dpi),
    absYmm: pxToMmRounded(bounds.top, dpi),
    wMm: pxToMmRounded(bounds.width, dpi),
    hMm: pxToMmRounded(bounds.height, dpi),
    rotationDeg: bounds.rotation,
    opacity: bounds.opacity
  };
}

/**
 * Process a single element for ingestion with absolute coordinates
 */
export function processElement(element: any, index: number, log: string[], dpi = 300, captureAll = false): TemplateLayer | null {
  // Extract alias from Alt text
  const altText = element.altText || element.alt || '';
  const alias = parseAlias(altText);

  // If captureAll is true, generate an alias for elements without one
  let finalAlias = alias;
  let surface: 'front' | 'spine' | 'back' = 'front';

  if (!alias && captureAll) {
    // Generate a unique alias based on element type and index
    const kind = determineElementKind(element.type);
    if (!kind) {
      log.push(`[ingest] Unsupported element type: ${element.type} (element ${index})`);
      return null;
    }
    finalAlias = `front__${kind}_${index}`;
    surface = 'front';
    log.push(`[ingest] Generated alias for element ${index}: ${finalAlias}`);
  } else if (!alias) {
    return null; // Skip elements without aliases if not capturing all
  } else {
    if (!isValidAlias(alias)) {
      log.push(`[ingest] Invalid alias format: ${alias} (element ${index})`);
      return null;
    }

    const parsedSurface = surfaceOf(alias);
    if (!parsedSurface) {
      log.push(`[ingest] Unknown surface for alias: ${alias} (element ${index})`);
      return null;
    }
    surface = parsedSurface;
  }

  // Determine element kind
  const kind = determineElementKind(element.type);
  if (!kind) {
    log.push(`[ingest] Unsupported element type: ${element.type} (element ${index})`);
    return null;
  }

  // Extract bounds and convert to absolute mm coordinates
  const bounds = convertElementBoundsAbsolute(element, dpi);

  // Extract type-specific properties
  const props = { ...bounds };

  switch (kind) {
    case 'text':
      const textProps = extractTextProps(element);
      props.text = textProps.text;
      props.fontFamily = textProps.fontFamily;
      props.fontSizePx = textProps.fontSize;
      props.fontWeight = textProps.fontWeight;
      props.fontStyle = textProps.fontStyle;
      props.color = textProps.color;
      props.textAlign = textProps.textAlign;
      props.textDecoration = textProps.textDecoration;
      props.lineHeight = textProps.lineHeight;
      props.letterSpacing = textProps.letterSpacing;
      props.textTransform = textProps.textTransform;
      props.regions = textProps.regions;
      break;
    case 'image':
      const imageProps = extractImageProps(element);
      props.imageRef = imageProps.imageRef;
      props.imageUrl = imageProps.imageUrl;
      props.mask = imageProps.maskFlag !== undefined ? imageProps.maskFlag : true;
      props.cropData = imageProps.cropData;
      props.filters = imageProps.filters;
      props.blendMode = imageProps.blendMode;
      break;
    case 'shape':
      const shapeProps = extractShapeProps(element);
      props.fill = shapeProps.fill;
      props.stroke = shapeProps.stroke;
      props.strokeWidthPx = shapeProps.strokeWidth;
      props.strokeDashArray = shapeProps.strokeDashArray;
      props.strokeLineCap = shapeProps.strokeLineCap;
      props.strokeLineJoin = shapeProps.strokeLineJoin;
      props.paths = shapeProps.paths;
      props.viewBox = shapeProps.viewBox;
      props.gradientFill = shapeProps.gradientFill;
      props.patternFill = shapeProps.patternFill;
      break;
  }

  // Create layer with absolute coordinates
  const layer: TemplateLayer = {
    id: finalAlias,
    surface,
    kind,
    lock: !!element.locked,
    metadata: {
      userEditable: !element.locked,
      alias: finalAlias
    },
    props
  };

  log.push(`[ingest] Processed ${kind} element: ${finalAlias} (${surface}) at ${bounds.absXmm.toFixed(1)},${bounds.absYmm.toFixed(1)}mm`);
  return layer;
}

/**
 * Recursively traverse elements including groups
 */
export function traverseElements(elements: any[], log: string[]): any[] {
  const allElements: any[] = [];

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    allElements.push(element);

    // If element is a group, recursively traverse its contents
    if (element.type === 'group' && element.contents) {
      log.push(`[ingest] Traversing group with ${element.contents.length} children`);
      const childElements = traverseElements(element.contents, log);
      allElements.push(...childElements);
    }
  }

  return allElements;
}

/**
 * Create complete descriptor with scale metadata
 */
export function createCoverDescriptor(
  slug: string,
  masterDesignId: string,
  name: string,
  sourcePageSizePx: { widthPx: number; heightPx: number },
  coverSizeMm: { widthMm: number; heightMm: number },
  layers: TemplateLayer[]
): CoverDescriptor {
  // Compute source scale: pixels per millimeter in the Master design
  const sourceScalePxPerMm = sourcePageSizePx.widthPx / coverSizeMm.widthMm;

  return {
    slug,
    dpi: 300, // Default DPI for reference
    coverSize: coverSizeMm,
    meta: {
      sourcePageSizePx,
      sourceScalePxPerMm
    },
    layers,
    createdAt: new Date().toISOString(),
    masterDesignId
  };
}

/**
 * Ingest Master design elements into template descriptor
 */
export function ingestMasterElements(elements: any[], captureAll = false): IngestionResult {
  const log: string[] = [];
  const layers: TemplateLayer[] = [];

  log.push(`[ingest] Starting ingestion of ${elements.length} top-level elements (captureAll: ${captureAll})`);

  // Recursively traverse all elements
  const allElements = traverseElements(elements, log);
  log.push(`[ingest] Found ${allElements.length} total elements (including group contents)`);

  // Process each element
  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const layer = processElement(element, i, log, 300, captureAll);
    if (layer) {
      layers.push(layer);
    }
  }

  const aliasCount = layers.length;
  const aliasNames = layers.map(l => l.metadata.alias);
  log.push(`[ingest] found ${aliasCount} aliases [${aliasNames.join(', ')}]`);

  if (aliasCount === 0 && !captureAll) {
    log.push(`[ingest] WARNING: No aliased elements found. Check Alt text format: alias:front__title`);
  } else if (captureAll) {
    log.push(`[ingest] Captured ${aliasCount} elements from design`);
  }

  return {
    layers,
    log,
    aliasCount,
    elementCount: allElements.length
  };
}
